import { useRouter } from 'next/router'
import { useEffect } from 'react'
import {
  GTM_CLICK_EVENT,
  getEventUserType,
  sendGTMBasicPageViewEvent,
  sendGTMCustomClickEvent,
} from '../../../../helpers/gtmHelper'
import { useCurrentUser } from '../../../../modules/context/UserContext'
import useTranslation from '../../../../modules/i18n/useTranslation'
import { STUDENT_BTN_ID, STUDENT_PAGE_VIEW_ID } from '../../../../modules/ids/gtm/student'
import useEvaluationStore from '../../../../store/useEvaluationStore'
import EvaluationStep1 from './EvaluationStep1'
import EvaluationStep2 from './EvaluationStep2'
import EvaluationStep3 from './EvaluationStep3'
import EvaluationStep4 from './EvaluationStep4'

export const STEP = {
  ONE: 'step-1',
  TWO: 'step-2',
  THREE: 'step-3',
  FOUR: 'step-4',
}

export interface IlessonData {
  id: number
  tutor: Tutor
  material_id: number
  course: Course
  formatted_start_time: string
  start_time: string
  is_rookie: boolean
  rookie_pointback_amount?: number
}

export interface Course {
  title: string
  subtitle: string
  free_talking: boolean
}

export interface Tutor {
  id: number
  name: string
  image_url: string
  school: string
  major: string
}

interface ILessonEvaluationModalBody {
  lessonId: number
  lessonData: IlessonData
  refetch?: () => void | any
  onClose?: any
  showReportModal?: any
  isClassroom?: boolean
}

const EvaluationModalBody = ({ lessonId, lessonData, refetch, onClose, isClassroom }: ILessonEvaluationModalBody) => {
  const { locale } = useTranslation()
  const router = useRouter()
  const { currentUser } = useCurrentUser()

  const tutorData = lessonData?.tutor
  const isRookie = lessonData?.is_rookie
  const pointBackAmount = lessonData?.rookie_pointback_amount

  const { step, setStep } = useEvaluationStore((state) => ({
    step: state.step,
    setStep: state.setStep,
  }))

  const getStepPayload = (step: string | undefined) => {
    switch (step) {
      case STEP.ONE:
        return 'Step-1-lesson-rating'
      case STEP.TWO:
        return 'Step-2-keyword-evaluation'
      case STEP.THREE:
        return 'Step-3-tutor-preference'
      case STEP.FOUR:
        return 'Step-4-review-material-network'
      default:
        return ''
    }
  }

  useEffect(() => {
    if (step) {
      const stepPayload = getStepPayload(step)

      sendGTMBasicPageViewEvent(
        STUDENT_PAGE_VIEW_ID.PORTAL_LESSON_EVALUATION_MODAL,
        getEventUserType(currentUser),
        locale,
        stepPayload,
        router.asPath
      )
    }
  }, [step])

  const moveTo = (step: string) => {
    setStep(step)
  }

  /**
   * Sends a custom click event to Google Tag Manager (GTM) with the given parameters.
   *
   * @param {string} buttonName - The name of the clicked button.
   */
  const sendClickEventToGTM = (buttonName: any) => {
    const eventType = GTM_CLICK_EVENT.CLICK_BUTTON_NEW
    const buttonId = STUDENT_BTN_ID.CLICK_LESSON_EVALUATION_BUTTON
    const eventUserType = getEventUserType(currentUser)
    const eventName = getStepPayload(step)
    const pagePath = getStepPayload(step)
    const payload = {
      btn_name: buttonName,
      lesson_id: lessonId,
      navigation: getStepPayload(step),
    }

    sendGTMCustomClickEvent(eventType, buttonId, buttonName, eventUserType, eventName, pagePath, payload)
  }

  return (
    <div className="relative flex h-full flex-col justify-between pt-[24px] sm:pt-[0px]">
      {step === STEP.ONE && (
        <EvaluationStep1
          next={() => moveTo(STEP.TWO)}
          lessonId={lessonId}
          tutorData={tutorData}
          refetch={() => refetch && refetch()}
          sendClickEventToGTM={sendClickEventToGTM}
          isRookie={isRookie}
          onClose={onClose}
        />
      )}
      {step === STEP.TWO && (
        <EvaluationStep2
          lessonId={lessonId}
          next={() => moveTo(STEP.THREE)}
          tutorData={tutorData}
          sendClickEventToGTM={sendClickEventToGTM}
          onClose={onClose}
        />
      )}
      {step === STEP.THREE && (
        <EvaluationStep3
          next={() => moveTo(STEP.FOUR)}
          tutorData={tutorData}
          lessonId={lessonId}
          sendClickEventToGTM={sendClickEventToGTM}
          onClose={onClose}
        />
      )}
      {step === STEP.FOUR && (
        <EvaluationStep4
          lessonId={lessonId}
          course={lessonData.course}
          tutorData={tutorData}
          isRookie={isRookie}
          pointBackAmount={pointBackAmount}
          refetch={() => refetch && refetch()}
          sendClickEventToGTM={sendClickEventToGTM}
          onClose={onClose}
        />
      )}
    </div>
  )
}

export default EvaluationModalBody
